/**
 * Periodized strength (gym) routines for the planner. The endurance bank
 * (workoutBank.ts) handles cardio; this gives each auto-suggested gym day a
 * real routine — different exercises chosen for the CURRENT training state:
 *
 *   phase  → strength focus   (Bompa periodization; Finish Strong for timing)
 *     base    → foundation    (anatomical adaptation, higher reps, technique)
 *     build   → max-strength   (heavy, low reps — strength without bulk)
 *     peak    → power          (explosive / plyometric, low volume)
 *     taper   → maintenance    (light, neural priming before the race)
 *     cutback → maintenance    (deload)
 *
 *   sport  → emphasis
 *     ski (NordicSki/RollerSki) → pull / double-poling power + trunk (Sandbakk
 *                                 & Holmberg; Stöggl & Holmberg)
 *     run                       → single-leg, posterior chain, plyo (Running
 *                                 Rewired — Dicharry)
 *     alpine (utförsåkning)     → max & eccentric leg strength, anaerobic box
 *                                 jumps, agility, core — mapped to Svenska
 *                                 Skidförbundets IronMan test battery
 *     other                     → balanced full body
 *
 * Output lines are PROSE (start with a letter) so toIcuWorkout() leaves them
 * intact and they stay valid intervals.icu text; only the leading duration line
 * ("1h …") is a real step. No standalone min/sek tokens — they'd be miscounted
 * as workout segments / flagged as bad units — full words are used instead.
 */
import { SportType, TrainingPhase } from "../types/training";

export type StrengthFocus = "foundation" | "max-strength" | "power" | "maintenance";
export type StrengthEmphasis = "general" | "ski" | "run" | "alpine";

export interface StrengthRoutine {
  id: string;
  name: string;          // descriptive — shown in the session description header
  emphasis: StrengthEmphasis;
  focus: StrengthFocus;
  scheme: string;        // set/rep guidance
  rationale: string;
  exercises: string[];   // prose exercise lines
}

const FOCUS_LABEL: Record<StrengthFocus, string> = {
  foundation: "Grundstyrka",
  "max-strength": "Maxstyrka",
  power: "Spänst & power",
  maintenance: "Underhåll",
};

export function strengthFocusLabel(f: StrengthFocus): string {
  return FOCUS_LABEL[f];
}

export function phaseToFocus(phase: TrainingPhase): StrengthFocus {
  switch (phase) {
    case "base":    return "foundation";
    case "build":   return "max-strength";
    case "peak":    return "power";
    case "taper":   return "maintenance";
    case "cutback": return "maintenance";
    default:        return "maintenance"; // race
  }
}

export function sportToEmphasis(sport: SportType): StrengthEmphasis {
  if (sport === "NordicSki" || sport === "RollerSki") return "ski";
  if (sport === "AlpineSki") return "alpine";
  if (sport === "Run") return "run";
  return "general";
}

const SCHEME: Record<StrengthFocus, string> = {
  foundation: "Upplägg: 2–3 set × 12–15 reps, kontrollerat tempo, teknik i fokus.",
  "max-strength": "Upplägg: tungt, 4–6 reps, full vila mellan set (2–3 minuters).",
  power: "Upplägg: explosivt, 3–5 reps, full återhämtning — kvalitet före trötthet.",
  maintenance: "Upplägg: lätt, 1–2 set, håll rörelsemönster vid liv utan trötthet.",
};

const r = (
  id: string, name: string, emphasis: StrengthEmphasis, focus: StrengthFocus,
  rationale: string, exercises: string[],
): StrengthRoutine => ({ id, name, emphasis, focus, scheme: SCHEME[focus], rationale, exercises });

export const STRENGTH_ROUTINES: StrengthRoutine[] = [
  // ── GENERAL ────────────────────────────────────────────────────────────────
  r("gen-foundation-a", "Helkropp grund A", "general", "foundation",
    "Anatomisk anpassning: helkropp, högre reps bygger senstyrka och teknik inför tyngre faser.",
    ["Knäböj", "Rumänsk marklyft", "Armhävningar", "Latsdrag", "Gående utfall", "Planka — 3 × 40 sekunder", "Ryggresningar"]),
  r("gen-foundation-b", "Helkropp grund B", "general", "foundation",
    "Variant för anti-monotoni — samma syfte, andra rörelser och vinklar.",
    ["Frontböj", "Höftlyft", "Hantelpress", "Sittande rodd", "Bulgarisk split-knäböj", "Sidoplanka — 3 × 30 sekunder/sida", "Fågelhund"]),
  r("gen-max-a", "Helkropp maxstyrka A", "general", "max-strength",
    "Maximal styrka med tunga baslyft — neural adaptation utan onödig muskelmassa.",
    ["Knäböj — 4 × 5", "Marklyft — 4 × 4", "Bänkpress — 4 × 5", "Skivstångsrodd — 4 × 5", "Axelpress — 3 × 5", "Hängande benlyft"]),
  r("gen-max-b", "Helkropp maxstyrka B", "general", "max-strength",
    "Andra baslyft för variation i belastningen.",
    ["Frontböj — 4 × 5", "Rumänsk marklyft — 4 × 5", "Chins — 4 × max", "Dips — 4 × 6", "Höftlyft — 4 × 6", "Pallof press"]),
  r("gen-power", "Helkropp power", "general", "power",
    "Omvandlar maxstyrka till spänst — explosiva lyft och hopp vid hög hastighet.",
    ["Box jumps — 5 × 3", "Knäböjshopp — 4 × 4", "Frivändning eller hängryck — 5 × 3", "Explosiva armhävningar", "Medicinbollkast", "Pallof press"]),
  r("gen-maintenance", "Helkropp underhåll", "general", "maintenance",
    "Bibehåller styrka med minimal trötthet inför tävling.",
    ["Knäböj — 2 × 8 lätt", "Höftlyft", "Armhävningar", "Latsdrag", "Planka — 2 × 40 sekunder", "Höft- och bröstryggsrörlighet"]),

  // ── SKI (dubbelstak, drag, bål) ──────────────────────────────────────────────
  r("ski-foundation-a", "Skidstyrka grund A", "ski", "foundation",
    "Sandbakk & Holmberg: stak- och bålstyrka är avgörande. Grundfas med drag och bål.",
    ["Latsdrag", "Stakdrag i kabel (dubbelstak)", "Triceps pushdown", "Knäböj", "Rumänsk marklyft", "Planka och sidoplanka", "Pallof press"]),
  r("ski-foundation-b", "Skidstyrka grund B", "ski", "foundation",
    "Variant med kroppsviktsdrag och bålrotation.",
    ["Chins", "Dips", "Sittande rodd", "Bulgarisk split-knäböj", "Höftlyft", "Hängande benlyft", "Anti-rotation i kabel"]),
  r("ski-max-a", "Skidstyrka max A", "ski", "max-strength",
    "Stöggl & Holmberg: tung dubbelstaks-specifik dragstyrka. Bål och armar i fokus.",
    ["Stakdrag i kabel tungt — 4 × 6", "Chins med vikt — 4 × 5", "Dips med vikt — 4 × 5", "Knäböj — 4 × 5", "Marklyft — 4 × 4", "Hängande benlyft"]),
  r("ski-max-b", "Skidstyrka max B", "ski", "max-strength",
    "Andra dragvinklar och pressrörelser för helhet.",
    ["Skivstångsrodd — 4 × 5", "Latsdrag tungt — 4 × 6", "Frontböj — 4 × 5", "Rumänsk marklyft — 4 × 5", "Axelpress — 4 × 5", "Pallof press tung"]),
  r("ski-power", "Skidstyrka power", "ski", "power",
    "Åkspecifik spänst: explosiv dubbelstak och kast i hög hastighet.",
    ["Explosiva kabelstak (dubbelstak) — 5 × 5", "Slamball overhead till golv — 5 × 6", "Box jumps — 5 × 3", "Skridskohopp — 4 × 6/sida", "Roterande medicinbollkast", "Plankvariationer"]),
  r("ski-maintenance", "Skidunderhåll", "ski", "maintenance",
    "Håller stak- och bålstyrka vid liv inför tävling.",
    ["Latsdrag lätt", "Stakkabel lätt", "Planka-serie", "Pallof press", "Bål- och axelrörlighet"]),

  // ── RUN (enbensstyrka, bakre kedja, plyo) ────────────────────────────────────
  r("run-foundation-a", "Löpstyrka grund A", "run", "foundation",
    "Dicharry (Running Rewired): enbensstabilitet och bakre kedja minskar skaderisk.",
    ["Bulgarisk split-knäböj", "Rumänsk marklyft enbens", "Höftlyft", "Vadpress", "Sidoutfall", "Planka", "Fågelhund"]),
  r("run-foundation-b", "Löpstyrka grund B", "run", "foundation",
    "Variant med steg-upp och höftstabilitet.",
    ["Gående utfall", "Step-ups", "Nordic hamstring", "Stående vadpress", "Höftabduktion (clamshell)", "Sidoplanka", "Döda buggen"]),
  r("run-max-a", "Löpstyrka max A", "run", "max-strength",
    "Tung styrka förbättrar löpekonomi utan att bygga onödig massa.",
    ["Knäböj — 4 × 5", "Marklyft — 4 × 4", "Bulgarisk split tung — 3 × 6/ben", "Höftlyft tung — 4 × 6", "Vadpress tung — 4 × 8", "Sidoplanka"]),
  r("run-max-b", "Löpstyrka max B", "run", "max-strength",
    "Bakre kedja och enbensvarianter.",
    ["Frontböj — 4 × 5", "Rumänsk marklyft — 4 × 5", "Step-up med vikt", "Nordic hamstring — 3 × 6", "Enbens vadpress", "Pallof press"]),
  r("run-power", "Löpstyrka power", "run", "power",
    "Plyometrik och reaktiv styrka: kort markkontakt, hög hastighet.",
    ["Box jumps — 5 × 3", "Enbenshopp — 4 × 5/ben", "A-skip och snabba fötter", "Utfallshopp — 4 × 6", "Pogo hops — 4 × 10", "Snabba vadhopp", "Anti-rotation"]),
  r("run-maintenance", "Löpunderhåll", "run", "maintenance",
    "Håller löpspecifik styrka och stabilitet vid liv inför tävling.",
    ["Höftlyft", "Split-knäböj lätt", "Vadpress", "Höftstabilitet (clamshell)", "Planka", "Höft- och fotledsrörlighet"]),

  // ── ALPINE (utförsåkning) — mapped to Svenska Skidförbundets IronMan-batteri:
  //   maxstyrka ben (1RM benböj), uthållighetsstyrka (submax benböj), maxstyrka
  //   överkropp (1RM bänk), chins, bålstyrka (Brutalbänk), anaerob kapacitet
  //   (90 s Kassehopp / boxhopp), koordination (Hexagon). Knäprehab genomgående.
  r("alpine-foundation-a", "Alpint grund A", "alpine", "foundation",
    "Förbereder IronMan-testerna: bygger uthållighetsstyrka (submax benböj) och bål, med knäprehab och koordination.",
    ["Knäböj — 3 × 15 (submax)", "Frontböj", "Step-ups med vikt", "Chins", "Bänkpress", "Brutalbänk / ryggresningar — 3 × 12", "Hexagon-agility — 3 × 6 varv", "Nordic hamstring (knäprehab)"]),
  r("alpine-foundation-b", "Alpint grund B", "alpine", "foundation",
    "Variant: enbensstyrka, bål och balans — grund för alpin belastning.",
    ["Bulgarisk split-knäböj", "Rumänsk marklyft", "Sittande rodd", "Dips", "Sidoplanka och Pallof press", "Balans på balanskudde", "Hexagon-agility — 3 × 6 varv"]),
  r("alpine-max-a", "Alpint maxstyrka A", "alpine", "max-strength",
    "Tränar IronMans maxstyrketester direkt: tung benböj (1RM), bänkpress (1RM) och chins.",
    ["Knäböj — 5 × 3–5 (tungt)", "Bänkpress — 4 × 4–6", "Viktade chins — 4 × 5", "Marklyft — 4 × 4", "Brutalbänk / ryggresningar — 3 × 10", "Nordic hamstring (knäprehab)"]),
  r("alpine-max-b", "Alpint maxstyrka B", "alpine", "max-strength",
    "Variant med excentrisk och isometrisk benstyrka — alpinspecifik (långa svängar).",
    ["Frontböj — 4 × 5", "Excentrisk knäböj (långsam neråt) — 4 × 5", "Bänkpress — 4 × 5", "Skivstångsrodd — 4 × 5", "Isometrisk skidsits mot vägg — 3 × 45 sekunder", "Bål: hängande benlyft"]),
  r("alpine-power", "Alpint power & anaerob", "alpine", "power",
    "Tränar IronMans anaeroba kapacitet (90 s Kassehopp) och koordination (Hexagon) plus explosiv benstyrka.",
    ["Boxhopp — 4 × 90 sekunder max (jfr Kassehopp)", "Knäböjshopp — 4 × 5", "Skridsko-/sidohopp — 4 × 6/sida", "Hexagon-agility — 4 × 6 varv", "Explosiv knäböj — 4 × 3", "Medicinbollkast"]),
  r("alpine-maintenance", "Alpint underhåll", "alpine", "maintenance",
    "Håller styrka, spänst och koordination vid liv under tävlingssäsong; lågdos och knäprehab.",
    ["Knäböj — 2 × 6 lätt", "Boxhopp — 3 × 5 (spänst)", "Hexagon-agility — 2 × 6 varv", "Bål: planka och Pallof", "Knä- och höftrörlighet och prehab"]),
];

/** Pick a routine for the current training state, rotating by seed for variety. */
export function pickStrengthRoutine(phase: TrainingPhase, sport: SportType, seedStr: string): StrengthRoutine {
  const focus = phaseToFocus(phase);
  const emphasis = sportToEmphasis(sport);
  let pool = STRENGTH_ROUTINES.filter(x => x.emphasis === emphasis && x.focus === focus);
  if (pool.length === 0) pool = STRENGTH_ROUTINES.filter(x => x.emphasis === "general" && x.focus === focus);
  if (pool.length === 0) pool = STRENGTH_ROUTINES.filter(x => x.focus === focus);
  // seedStr is "weekIndex-dayOffset"; week*7+day rotates variants week-to-week
  // (and day-to-day for two gym days in the same week).
  const [wk = 0, day = 0] = (seedStr.match(/\d+/g) ?? []).map(Number);
  const seed = wk * 7 + day;
  return pool[seed % pool.length];
}

/** Build the intervals.icu gym description: a duration step + prose routine. */
export function buildGymDescription(routine: StrengthRoutine, durationLabel: string): string {
  return [
    `${durationLabel} styrketräning · ${routine.name}`,
    routine.scheme,
    ...routine.exercises,
    "Komplement till uthållighetsträningen.",
  ].join("\n");
}
